import type { HearingLossRecipe } from 'common/interfaces/hearingLoss';
import type { IProcessAction } from 'common/interfaces/IProcessAction';
import React, { useEffect, useState } from 'react';
import { Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import type { IRequest } from '../../../../../common/interfaces/IRequest';
import type { IReduxState } from '../../../../configureStore';
import { servicedeskPhoneNumber } from '../../../../../common/constants';
import { PatientDataError } from '../../../../../common/enums/PatientDataError';
import { RequestStatus } from '../../../../../common/enums/RequestStatus';
import { ServiceCode } from '../../../../../common/enums/ServiceCode';
import { getHearingLossRecipes } from '../../../../services/api/hearingLossApiService';
import { getLastOsasRequestWithMraReferralLetter } from '../../../../utils/getLastOsasc2RequestWithMraReferralLetter';
import { getLastSomnologyRequest } from '../../../../utils/getLastSomnologyRequest';
import { HearingLossRecipeCard } from '../../../pages/HearingLoss/HearingLossRecipeCard';
import { OsasFlexJouwVerwijzingCard } from '../../../pages/OsasFlex/components/OsasFlexJouwVerwijzingCard';
import { OsasFlexMraActionCard } from '../../../pages/OsasFlex/components/OsasFlexMraActionCard';
import { OsasFlexQuestionnairesCard } from '../../../pages/OsasFlex/components/OsasFlexQuestionnairesCard';
import { RequestStatusCard } from '../../../pages/RequestStatusCard';
import { HeroText } from '../../atoms/heroText/HeroText';
import { PageTitle } from '../../atoms/pageTitle/PageTitle';
import { Paragraph } from '../../atoms/paragraph/Paragraph';
import { Alert } from '../../molecules/alert/Alert';
import { Article } from '../../molecules/article/article';
import { Hero } from '../../molecules/hero/Hero';
import { PageSection } from '../../molecules/pageSection/PageSection';
import { EmptyStateCard } from '../cards/EmptyStateCard';
import { OsasCards } from '../osasCards/OsasCards';
import { OsasQuestionnairesCard } from '../osasCards/OsasQuestionnairesCard';

interface IWelcomeProps {
    error?: PatientDataError;
}

export function Welcome(props: IWelcomeProps) {
    const { error } = props;
    const [hearingLossRecipes, setHearingLossRecipes] = useState<HearingLossRecipe[]>([]);
    const patientName = useSelector((state: IReduxState) => state.authentication.user?.displayName);

    const actions = useSelector((state: IReduxState) => state.authentication.actions);
    const requestProcesses = useSelector((state: IReduxState) => state.authentication.requestProcesses);

    const hasOsasPatientQuestionnaire = !!actions?.find(
        (action: IProcessAction) =>
            action.service.code === ServiceCode.OSAS &&
            action.activities.some((activity) =>
                ['OSASMijnKsyosEssQuestionnaire', 'OSASMijnKsyosHsdqQuestionnaire', 'OSASLifestyleForm'].includes(
                    activity.customControlCode
                )
            )
    );

    const dermaOthRequest = requestProcesses?.find(
        (request: IRequest) => request.service.code === ServiceCode.DermaOth
    );

    const gtoRequest = requestProcesses?.find((request: IRequest) => request.service.code === ServiceCode.GTO);
    const lastSomnologyRequest = getLastSomnologyRequest(requestProcesses, true);
    const mraReferralLetterRequest = getLastOsasRequestWithMraReferralLetter(requestProcesses);
    const userHasOsasMraReferralLetter = useSelector(
        (state: IReduxState) => state.authentication.userHasOsasMraReferralLetter
    );
    const hasOsasMraTreatment = useSelector((state: IReduxState) => state.authentication.hasOsasMraTreatment);

    useEffect(() => {
        loadHearingLossRecipes();
    }, []);

    return (
        <>
            <PageSection>
                <Article>
                    <PageTitle> Home </PageTitle>
                    {!error && (
                        <HeroText>
                            <strong> Welkom, {patientName ?? ''} </strong> dit is jouw persoonlijke Ksyos omgeving
                        </HeroText>
                    )}

                    {error === PatientDataError.generalError && (
                        <>
                            <HeroText>Dit is jouw persoonlijke Ksyos omgeving.</HeroText>
                            <Alert
                                alertHeading="Er is iets fout gegaan"
                                variant="warning"
                            >
                                <Paragraph>
                                    De persoonlijke omgeving kon niet succesvol worden ingeladen. Neem voor hulp contact
                                    op met de Ksyos servicedesk
                                </Paragraph>
                                <a
                                    aria-label="Bel Ksyos servicedesk"
                                    href={`tel:${servicedeskPhoneNumber}`}
                                >
                                    {servicedeskPhoneNumber}
                                </a>
                            </Alert>
                        </>
                    )}

                    {error === PatientDataError.noDataAvailable && (
                        <HeroText>
                            Dit jouw persoonlijke Ksyos omgeving. Op dit moment zijn er geen (actieve) onderzoeken.
                        </HeroText>
                    )}
                </Article>
            </PageSection>
            <StyledHero
                fullWidth
                imgUrlDesktop="/assets/images/thuisconsult-home-large.svg"
                imgUrlMobile="/assets/images/thuisconsult-home-small.svg"
            >
                <Row>{renderCards()}</Row>
            </StyledHero>
        </>
    );

    function renderCards() {
        const showingCardStatus = [RequestStatus.requested, RequestStatus.active, RequestStatus.accepted];

        const shouldShowRequestCard = (request: IRequest) => showingCardStatus.includes(request.status);

        const renderedCards: React.JSX.Element[] = [];

        if (gtoRequest && shouldShowRequestCard(gtoRequest)) {
            renderedCards.push(
                <RequestStatusCard
                    key={0}
                    request={gtoRequest}
                />
            );
        }
        if (dermaOthRequest && shouldShowRequestCard(dermaOthRequest)) {
            renderedCards.push(
                <RequestStatusCard
                    key={1}
                    request={dermaOthRequest}
                />
            );
        }
        if (lastSomnologyRequest && shouldShowRequestCard(lastSomnologyRequest)) {
            renderedCards.push(
                <OsasCards
                    key={2}
                    request={lastSomnologyRequest}
                />
            );
        }

        renderedCards.push(<OsasQuestionnairesCard key={3} />);

        if (hasOsasPatientQuestionnaire) {
            renderedCards.push(<OsasFlexQuestionnairesCard key={4} />);
        }

        if (mraReferralLetterRequest || hasOsasMraTreatment) {
            renderedCards.push(<OsasFlexMraActionCard key={5} />);
            renderedCards.push(
                <OsasFlexJouwVerwijzingCard
                    key={6}
                    request={mraReferralLetterRequest}
                    userHasOsasMraReferralLetter={userHasOsasMraReferralLetter}
                />
            );
        }

        hearingLossRecipes.forEach((recipe) => {
            renderedCards.push(
                <HearingLossRecipeCard
                    key={recipe.processActivityId}
                    performedDatetime={recipe.performedDatetime}
                    processActivityId={recipe.processActivityId}
                    profession={recipe.profession}
                />
            );
        });

        if (renderedCards.length === 0) {
            return <EmptyStateCard key={8} />;
        }

        return renderedCards;
    }

    async function loadHearingLossRecipes() {
        const recipes = await getHearingLossRecipes();
        setHearingLossRecipes(recipes);
    }
}

const StyledHero = styled(Hero)`
    /*
Need to add this custom styling as the background image is different from the image in the
Login Page which also uses a hero, but now the text needs to be positioned differently
*/
    h2 {
        top: 2rem;
        position: absolute;
    }

    @media screen and (min-width: 768px) {
        h2 {
            position: relative;
            margin-bottom: 3rem;
        }
    }

    min-height: 350px;
    margin-bottom: 24px;

    @media screen and (max-width: 768px) {
        // no cards : background position is bottom and height is max 350px
    }
`;
